import AdminContextMenu from './admin-context-menu';
import AuthorContextMenu from './author-context-menu';
import NotAuthorContextMenu from './not-author-context-menu';
import {Comment, useAppContext} from '../../../app-context';
import {computeContextMenuPosition} from '../../../utils/context-menu-position';
import {useEffect, useLayoutEffect, useRef} from 'react';

type Props = {
    comment: Comment;
    close: () => void;
    toggleEdit: () => void;
};
const CommentContextMenu: React.FC<Props> = ({comment, close, toggleEdit}) => {
    const {member, isAdmin} = useAppContext();
    const isAuthor = member && comment.member?.uuid === member?.uuid;
    const element = useRef<HTMLDivElement>(null);
    const innerElement = useRef<HTMLDivElement>(null);

    // [Unleash] メニューを iframe viewport 基準の fixed 配置にする。
    // 本家は…ボタン基準の絶対配置のため、ボタンが iframe の端や中央寄りにあると
    // メニューが左右で見切れていた。…ボタンの右上/右下に出しつつ、右辺は iframe の
    // 右縁・上辺は…ボタンの y に揃える（下が足りなければ上方向へ展開）。
    // 計算ロジックは utils/context-menu-position.ts（単体テストあり）。
    useLayoutEffect(() => {
        const inner = innerElement.current;
        const wrapper = element.current;
        if (!inner || !wrapper) {
            return;
        }

        const doc = wrapper.ownerDocument;
        const win = doc.defaultView || window;
        // …ボタンを含む親（MoreButton の wrapper）を基準にする。なければ自要素。
        const trigger = wrapper.parentElement ?? wrapper;

        const reposition = () => {
            const triggerRect = trigger.getBoundingClientRect();
            const menuRect = inner.getBoundingClientRect();
            const {top, right} = computeContextMenuPosition({
                trigger: {top: triggerRect.top, bottom: triggerRect.bottom},
                menu: {height: menuRect.height},
                viewport: {width: win.innerWidth, height: win.innerHeight}
            });
            inner.style.position = 'fixed';
            inner.style.top = `${top}px`;
            inner.style.right = `${right}px`;
            inner.style.left = 'auto';
            inner.style.bottom = 'auto';
        };

        reposition();
        win.addEventListener('resize', reposition, {passive: true});
        win.addEventListener('scroll', reposition, {passive: true, capture: true});

        return () => {
            win.removeEventListener('resize', reposition);
            win.removeEventListener('scroll', reposition, {capture: true} as any);
        };
    }, []);

    useEffect(() => {
        const listener = () => {
            close();
        };

        // We need to listen for the window outside the iframe, and also the iframe window events
        window.addEventListener('click', listener, {passive: true});
        const el = element.current?.ownerDocument?.defaultView;

        if (el && el !== window) {
            el.addEventListener('click', listener, {passive: true});
        }

        return () => {
            window.removeEventListener('click', listener, {passive: true} as any);
            if (el && el !== window) {
                el.removeEventListener('click', listener, {passive: true} as any);
            }
        };
    }, [close]);

    useEffect(() => {
        const listener = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };
        // For keydown, we only need to listen to the main window, because we pass the events
        // manually in the Iframe component
        window.addEventListener('keydown', listener, {passive: true});

        return () => {
            window.removeEventListener('keydown', listener, {passive: true} as any);
        };
    }, [close]);

    // Prevent closing the context menu when clicking inside of it
    const stopPropagation = (event: React.SyntheticEvent) => {
        event.stopPropagation();
    };

    // [Unleash] right-0 で…ボタンの右辺に右寄せ（左方向へ展開）。iframe 右縁に近づける
    // [Unleash] 位置は useLayoutEffect で fixed 指定する（top/right を JS 計算）
    const menuClassName = 'fixed z-10 w-52 rounded-lg bg-white p-1 font-sans text-sm shadow-lg outline-0 dark:bg-neutral-800 dark:text-white';

    let contextMenu = null;
    if (comment.status === 'published') {
        if (isAdmin) {
            contextMenu = <AdminContextMenu close={close} comment={comment} showAuthorActions={!!isAuthor} toggleEdit={toggleEdit}/>;
        } else if (isAuthor) {
            contextMenu = <AuthorContextMenu close={close} comment={comment} toggleEdit={toggleEdit} />;
        } else {
            contextMenu = <NotAuthorContextMenu close={close} comment={comment}/>;
        }
    } else {
        if (isAdmin) {
            contextMenu = <AdminContextMenu close={close} comment={comment}/>;
        } else {
            return null;
        }
    }

    return (
        <div ref={element} className="relative" data-testid="comment-context-menu" onClick={stopPropagation}>
            <div ref={innerElement} className={menuClassName} data-testid="comment-context-menu-inner">
                {contextMenu}
            </div>
        </div>
    );
};

export default CommentContextMenu;
